/**
 * Advanced Prompt Editor Component
 * Features:
 * - Monaco Editor (VS Code editor) for syntax highlighting
 * - Drag-and-drop inputs into prompt
 * - Input type selection (expert, indicator, static_text, etc.)
 * - Input suggestions and autocomplete
 * - Template syntax support ({{input_name}})
 * - Real-time validation
 * - Multi-line editing with proper formatting
 */

import React, { useRef, useState, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Info as InfoIcon,
  DragIndicator as DragIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  OpenInFull as ExpandIcon,
  CloseFullscreen as CollapseIcon,
  PlayArrow as TestIcon,
} from '@mui/icons-material';
import { expertApi, snippetApi, sharedInputApi } from '../../services/api';
import type { SnippetListItem } from '../../types';
import { InputConfigFields, type ExpertInput } from './InputConfigFields';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

// Input types matching backend AI/input_schema.py
export type InputType = 
  | 'event_payload'
  | 'expert'
  | 'indicator'
  | 'economic_calendar'
  | 'news_data'
  | 'cot_data'
  | 'options_data'
  | 'economic_indicators'
  | 'screener_snapshot'
  | 'account_data'
  | 'open_positions'
  | 'pending_orders'
  | 'symbol_data'
  | 'trading_journal'
  | 'price_data'
  | 'previous_expert_results'
  | 'current_time'
  | 'static_text'
  | 'chart_image'
  | 'chart_image_file'
  // Storage Types
  | 'storage_text'
  | 'storage_file'
  // Global Storage Types
  | 'global_storage_text'
  | 'global_storage_file'
  // Knowledge Graph
  | 'knowledge_graph'
  // Model Lab
  | 'model_lab_prediction'
  // OpenBB Integration Types
  | 'stock_fundamentals'
  | 'openbb_news'
  | 'openbb_options'
  | 'openbb_forex'
  | 'openbb_crypto'
  | 'openbb_etf'
  | 'openbb_economic';

export interface InputDefinition {
  name: string;
  type: InputType;
  description?: string;
  
  // Type-specific fields
  // Expert
  expert_id?: number;
  expert_name?: string;
  
  // Indicator
  indicator_name?: string;
  indicators?: Array<{
    indicator_name: string;
    params?: Record<string, any>;
  }>;
  use_indicator_set?: boolean;  // Flag to use predefined indicator set
  indicator_set_id?: number;     // Reference to IndicatorSet
  symbol?: string;  // Legacy single symbol support (still used by some types)
  symbols?: string[];  // Multi-symbol support for Economic Calendar, COT Data
  timeframe?: string;
  
  // Fundamental Data
  data_type?: string;
  source?: string;
  
  // Economic Calendar, News Data, COT Data
  count?: number;  // Record limit - also used for positions and journal entries
  impact?: string;  // Legacy single impact support
  impacts?: string[];  // Multi-impact support for Economic Calendar
  duration_days?: number;  // Duration filter in days for Economic Calendar, News Data, COT Data
  tags?: string[];  // Topic tags filter for News Data
  
  // Account Data, Open Positions, Trading Journal
  account_id?: string;  // Legacy single account
  account_ids?: string[];  // New multi-account support
  currency?: string;
  
  // Trading Journal
  entry_id?: string;
  
  // Chart Image
  width?: number;  // Chart width in pixels
  height?: number;  // Chart height in pixels
  theme?: string;  // Chart theme (dark/light)
  
  // Static Text
  label?: string;
  text?: string;
  
  // Options Data (Yahoo Finance)
  expiration_date?: string;  // Specific expiration date (YYYY-MM-DD)
  option_type?: 'calls' | 'puts' | 'both';  // Type of options to fetch
  near_the_money?: number;  // Number of strikes around ATM
  max_expirations?: number;  // Max number of expirations to fetch
  include_greeks?: boolean;  // Whether to include Greeks
  
  // Economic Indicators (FRED)
  currencies?: string[];  // Multiple currencies for comparison (USD, EUR, GBP, JPY, etc.)
  economic_indicator_names?: string[];  // Specific FRED indicators (GDP, CPI, Unemployment, etc.)
  include_comparison?: boolean;  // Include change vs previous period

  // Screener Snapshot
  filters?: Array<{
    field: string;
    op: 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'icontains';
    value?: any;
    field_ref?: string;
  }>;
  sort?: {
    field?: string;
    direction?: 'asc' | 'desc';
  };
  asset_class?: string;
  page?: number;
  page_size?: number;
  
  // OpenBB Integration Fields
  data_types?: string[];  // Types of data to fetch (profile, income, ratios, etc.)
  period?: string;  // annual or quarterly for financial statements
  limit?: number;  // Number of records/periods
  days_back?: number;  // Days of historical data
  strikes_count?: number;  // Number of option strikes
  countries?: string[];  // Countries for economic data
  holdings_limit?: number;  // Number of ETF holdings
  include_forecast?: boolean;  // Include forecasts for economic data
  
  // OpenBB Enhanced Filters
  provider?: string;  // Specific data provider (fmp, polygon, benzinga, etc.)
  source_type?: 'world' | 'company';  // News source type
  include_content?: boolean;  // Include full article content for news
  start_date?: string;  // Start date filter (YYYY-MM-DD)
  end_date?: string;  // End date filter (YYYY-MM-DD)
  expiration?: string;  // Options expiration date
  include_unusual?: boolean;  // Include unusual options activity
  pairs?: string[];  // Multiple forex pairs
  bars_count?: number;  // Number of price bars for historical data
  crypto_symbols?: string[];  // Multiple crypto symbols
  include_sectors?: boolean;  // Include ETF sector breakdown
  include_countries?: boolean;  // Include ETF country breakdown
  include_calendar?: boolean;  // Include economic calendar
  economic_indicators?: string[];  // Economic indicators list (gdp, cpi, etc.)
  fred_series?: string[];  // Specific FRED series IDs
  include_estimates?: boolean;  // Include analyst estimates
  
  // Knowledge Graph
  query_type?: 'currency_context' | 'entity_context' | 'causal_chain' | 'historical_effects';
  entity?: string;  // Entity name for entity_context
  event_id?: number;  // Event ID for causal_chain
  event_type?: string;  // Event type for historical_effects
  direction?: 'backward' | 'forward';  // Direction for causal_chain
  max_depth?: number;  // Max chain hops
  
  // Storage
  storage_name?: string;  // Specific storage item name
  storage_tags?: string[];  // Tags to filter storage items
  match_all_tags?: boolean;  // AND logic for tags
  include_metadata?: boolean;  // Include metadata in output
  extract_text?: boolean;  // Extract text from file (storage_file)

  // Model Lab Prediction
  deployment_id?: number;       // Required: which deployment to call
  rows?: number;                // How many most-recent rows to predict (default 1)
  lookback_bars?: number;       // Override recipe lookback
  pipeline?: string;            // Override recipe pipeline
  include_features?: boolean;   // Include computed features in output
  include_probabilities?: boolean; // Include class probabilities (default true)
}

// Expert option for dropdown
export interface ExpertOption {
  id: number;
  name: string;
}

// Shared Input type for display in editor
export interface SharedInputDisplay {
  name: string;
  description: string;
  input_type: string;
}

interface PromptEditorProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  helperText?: string;
  disabled?: boolean;
  minHeight?: number;
  availableInputs?: InputDefinition[]; // Available inputs with types
  onInputsChange?: (inputs: InputDefinition[]) => void; // Callback when inputs are added/removed
  availableExperts?: ExpertOption[]; // Available experts for expert input type dropdown
  editorRef?: React.RefObject<any>; // Expose editor ref for external snippet insertion
  showInputs?: boolean; // Show expert input controls
  showSnippets?: boolean; // Show snippet browser
  defaultShowSnippetPanel?: boolean; // Open snippet browser by default
  hideEditor?: boolean; // Hide the text editor (for CODE experts - inputs only mode)
  sharedInputs?: SharedInputDisplay[]; // Shared inputs from AI System level
  systemId?: number; // AI System ID for testing inputs
}

const INPUT_TYPE_LABELS: Record<InputType, string> = {
  event_payload: 'Trigger Event',
  expert: 'Expert Output',
  indicator: 'Indicator',
  economic_calendar: 'Economic Calendar',
  news_data: 'News Data',
  cot_data: 'COT Data',
  options_data: 'Options Data',
  economic_indicators: 'Economic Indicators',
  screener_snapshot: 'Screener Snapshot',
  account_data: 'Account Data',
  open_positions: 'Open Positions',
  pending_orders: 'Pending Orders',
  symbol_data: 'Symbol Data',
  trading_journal: 'Trading Journal',
  price_data: 'Price Data',
  previous_expert_results: 'Previous Expert Results',
  current_time: 'Current Time',
  static_text: 'Static Text',
  chart_image: 'Chart Image',
  chart_image_file: 'Chart Image (File)',
  // Storage
  storage_text: 'Storage Text',
  storage_file: 'Storage File',
  // Global Storage
  global_storage_text: 'Global Storage Text',
  global_storage_file: 'Global Storage File',
  // Knowledge Graph
  knowledge_graph: 'Knowledge Graph',
  // Model Lab
  model_lab_prediction: 'Model Lab Prediction',
  // OpenBB Integration
  stock_fundamentals: 'Stock Fundamentals',
  openbb_news: 'Financial News (OpenBB)',
  openbb_options: 'Options Chain (OpenBB)',
  openbb_forex: 'Forex Data (OpenBB)',
  openbb_crypto: 'Crypto Data (OpenBB)',
  openbb_etf: 'ETF Data (OpenBB)',
  openbb_economic: 'Economic Data (OpenBB)',
};

// Input type grouping for enhanced dropdown
export interface InputTypeOption {
  value: InputType;
  label: string;
  description: string;
  group: string;
}

export const INPUT_TYPE_GROUPS: { name: string; icon: string; color: string }[] = [
  { name: 'Runtime Context', icon: 'CTX', color: '#f97316' },
  { name: 'Basic', icon: '📝', color: '#6b7280' },
  { name: 'Technical Analysis', icon: '📊', color: '#8b5cf6' },
  { name: 'Market Data', icon: '📈', color: '#3b82f6' },
  { name: 'Account & Trading', icon: '💼', color: '#10b981' },
  { name: 'Knowledge Graph', icon: '🧠', color: '#ec4899' },
  { name: 'Model Lab', icon: '🤖', color: '#0ea5e9' },
  { name: 'Storage', icon: '💾', color: '#6366f1' },
  { name: 'Global Storage', icon: '🌍', color: '#059669' },
  { name: 'OpenBB Platform', icon: '🌐', color: '#f59e0b' },
];

/**
 * Input types that are only available to staff/admin users.
 * Non-admins will not see these in the input-type dropdown.
 */
export const ADMIN_ONLY_INPUT_TYPES: ReadonlySet<InputType> = new Set<InputType>([
  'model_lab_prediction',
  'stock_fundamentals',
  'openbb_news',
  'openbb_options',
  'openbb_forex',
  'openbb_crypto',
  'openbb_etf',
  'openbb_economic',
]);

export const INPUT_TYPE_OPTIONS: InputTypeOption[] = [
  // Basic
  { value: 'static_text', label: 'Static Text', description: 'User-provided text or data', group: 'Basic' },
  { value: 'expert', label: 'Expert Output', description: 'Output from another AI expert', group: 'Basic' },
  { value: 'current_time', label: 'Current Time', description: 'Current UTC timestamp', group: 'Basic' },
  { value: 'previous_expert_results', label: 'Previous Results', description: 'Historical expert execution results', group: 'Basic' },

  // Runtime Context
  { value: 'event_payload', label: 'Trigger Event', description: 'Sanitized event details from an event-triggered execution', group: 'Runtime Context' },

  // Technical Analysis
  { value: 'indicator', label: 'Technical Indicator', description: 'RSI, MACD, Bollinger Bands, etc.', group: 'Technical Analysis' },
  { value: 'chart_image', label: 'Chart Image (URL)', description: 'Chart with indicators as image URL', group: 'Technical Analysis' },
  { value: 'chart_image_file', label: 'Chart Image (File)', description: 'Chart with indicators as file attachment', group: 'Technical Analysis' },
  { value: 'price_data', label: 'Price Data', description: 'OHLCV price information', group: 'Technical Analysis' },
  
  // Market Data
  { value: 'economic_calendar', label: 'Economic Calendar', description: 'Economic events & scheduled releases', group: 'Market Data' },
  { value: 'news_data', label: 'News Data', description: 'Latest market news headlines', group: 'Market Data' },
  { value: 'cot_data', label: 'COT Data', description: 'Commitment of Traders reports', group: 'Market Data' },
  { value: 'options_data', label: 'Options Data', description: 'Options chain data (Yahoo Finance)', group: 'Market Data' },
  { value: 'economic_indicators', label: 'Economic Indicators', description: 'GDP, CPI, Unemployment (FRED)', group: 'Market Data' },
  { value: 'screener_snapshot', label: 'Screener Snapshot', description: 'Data warehouse market screener results', group: 'Market Data' },
  { value: 'symbol_data', label: 'Symbol Data', description: 'Trading symbol information', group: 'Market Data' },
  
  // Account & Trading
  { value: 'account_data', label: 'Account Data', description: 'Trading account information', group: 'Account & Trading' },
  { value: 'open_positions', label: 'Open Positions', description: 'Current trading positions', group: 'Account & Trading' },
  { value: 'pending_orders', label: 'Pending Orders', description: 'Pending limit/stop orders', group: 'Account & Trading' },
  { value: 'trading_journal', label: 'Trading Journal', description: 'Journal entries and notes', group: 'Account & Trading' },
  
  // Knowledge Graph
  { value: 'knowledge_graph', label: 'Knowledge Graph', description: 'Causal chains, currency narratives, historical effects', group: 'Knowledge Graph' },
  
  // Model Lab
  { value: 'model_lab_prediction', label: 'Model Lab Prediction', description: 'Live prediction from a deployed ML model (auto-fetches features)', group: 'Model Lab' },
  
  // Storage
  { value: 'storage_text', label: 'Storage Text', description: 'Read text from AI System Storage', group: 'Storage' },
  { value: 'storage_file', label: 'Storage File', description: 'Read files from AI System Storage', group: 'Storage' },
  
  // Global Storage
  { value: 'global_storage_text', label: 'Global Storage Text', description: 'Read text from Global Storage (user-level)', group: 'Global Storage' },
  { value: 'global_storage_file', label: 'Global Storage File', description: 'Read files from Global Storage (user-level)', group: 'Global Storage' },
  
  // OpenBB Platform
  { value: 'stock_fundamentals', label: 'Stock Fundamentals', description: 'Company financials, ratios, earnings', group: 'OpenBB Platform' },
  { value: 'openbb_news', label: 'Financial News', description: 'Multi-source news (FMP, Benzinga, etc.)', group: 'OpenBB Platform' },
  { value: 'openbb_options', label: 'Options Chain', description: 'Options data with Greeks', group: 'OpenBB Platform' },
  { value: 'openbb_forex', label: 'Forex Data', description: 'Currency pair data and rates', group: 'OpenBB Platform' },
  { value: 'openbb_crypto', label: 'Crypto Data', description: 'Cryptocurrency prices and data', group: 'OpenBB Platform' },
  { value: 'openbb_etf', label: 'ETF Data', description: 'ETF holdings and information', group: 'OpenBB Platform' },
  { value: 'openbb_economic', label: 'Economic Data', description: 'GDP, CPI, interest rates (60+ sources)', group: 'OpenBB Platform' },
];

const INPUT_TYPE_COLORS: Record<InputType, any> = {
  event_payload: 'warning',
  expert: 'primary',
  indicator: 'secondary',
  economic_calendar: 'info',
  news_data: 'info',
  cot_data: 'info',
  options_data: 'info',
  economic_indicators: 'info',
  screener_snapshot: 'info',
  account_data: 'success',
  open_positions: 'warning',
  pending_orders: 'warning',
  symbol_data: 'error',
  trading_journal: 'default',
  price_data: 'info',
  previous_expert_results: 'primary',
  current_time: 'secondary',
  static_text: 'default',
  chart_image: 'secondary',
  chart_image_file: 'secondary',
  // Storage
  storage_text: 'info',
  storage_file: 'info',
  // Global Storage
  global_storage_text: 'success',
  global_storage_file: 'success',
  // Knowledge Graph
  knowledge_graph: 'error',
  // Model Lab
  model_lab_prediction: 'primary',
  // OpenBB Integration
  stock_fundamentals: 'warning',
  openbb_news: 'warning',
  openbb_options: 'warning',
  openbb_forex: 'warning',
  openbb_crypto: 'warning',
  openbb_etf: 'warning',
  openbb_economic: 'warning',
};

export const PromptEditor: React.FC<PromptEditorProps> = ({
  value,
  onChange,
  label,
  helperText,
  disabled = false,
  minHeight = 300,
  availableInputs = [],
  onInputsChange,
  availableExperts = [],
  editorRef: externalEditorRef,
  showInputs = true,
  showSnippets = true,
  defaultShowSnippetPanel = false,
  hideEditor = false,
  sharedInputs = [],
  systemId,
}) => {
  const editorRef = useRef<any>(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showInputsPanel, setShowInputsPanel] = useState(true);
  const [detectedVariables, setDetectedVariables] = useState<string[]>([]);
  const [customInputDialogOpen, setCustomInputDialogOpen] = useState(false);
  const [newInputName, setNewInputName] = useState('');
  const [newInputType, setNewInputType] = useState<InputType>('static_text');
  const [newInputDescription, setNewInputDescription] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [editorHeight, setEditorHeight] = useState(minHeight);

  // Keep editor height in sync if the incoming minHeight prop changes
  // (e.g. parent switches between fullscreen/normal/mobile layouts).
  useEffect(() => {
    setEditorHeight((prev) => (prev < minHeight ? minHeight : prev));
  }, [minHeight]);

  // Auth — used to hide admin-only input types for regular users
  const { user } = useAuth();
  const isAdmin = user?.is_staff === true;
  const {
    data: availableInputTypesResponse,
    isLoading: availableInputTypesLoading,
    isError: availableInputTypesError,
    error: availableInputTypesQueryError,
  } = useQuery({
    queryKey: ['expert-input-types'],
    queryFn: () => expertApi.getAvailableInputTypes(),
    staleTime: 5 * 60 * 1000,
    enabled: showInputs,
  });
  const availableInputTypes = availableInputTypesResponse?.types;
  const visibleInputTypeOptions = React.useMemo(
    () => {
      const baseOptions = isAdmin
        ? INPUT_TYPE_OPTIONS
        : INPUT_TYPE_OPTIONS.filter(o => !ADMIN_ONLY_INPUT_TYPES.has(o.value));

      if (availableInputTypesError) {
        return [];
      }

      if (!availableInputTypes) {
        return availableInputTypesLoading ? [] : baseOptions;
      }

      const allowedValues = new Set(availableInputTypes.map(type => type.value));
      return baseOptions.filter(option => allowedValues.has(option.value));
    },
    [availableInputTypes, availableInputTypesError, availableInputTypesLoading, isAdmin]
  );
  const selectedVisibleInputTypeOption = visibleInputTypeOptions.find(opt => opt.value === newInputType) || null;
  const inputTypesUnavailableMessage = availableInputTypesError
    ? (availableInputTypesQueryError as any)?.response?.data?.detail
      || (availableInputTypesQueryError as any)?.response?.data?.error
      || 'No expert input handlers are available for your current subscription.'
    : 'No expert input handlers are available for your current subscription.';
  
  // Snippet state
  const [snippets, setSnippets] = useState<SnippetListItem[]>([]);
  const [loadingSnippets, setLoadingSnippets] = useState(false);
  const [showSnippetPanel, setShowSnippetPanel] = useState(defaultShowSnippetPanel);
  
  // Test input state
  const [testResultDialogOpen, setTestResultDialogOpen] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    data?: string;
    data_type?: string;
    data_length?: number;
    truncated?: boolean;
    execution_time_ms: number;
    error?: string;
    error_type?: string;
    traceback?: string;
  } | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  
  // Debug: Log availableExperts
  useEffect(() => {
    console.log('🔍 [PromptEditor] availableExperts:', availableExperts);
  }, [availableExperts]);
  
  // Edit mode state
  const [editingInput, setEditingInput] = useState<InputDefinition | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Dynamic fields for different input types
  const [dynamicFields, setDynamicFields] = useState<Record<string, any>>({});
  
  // Selected indicators (synced from InputConfigFields via onConfigChange)
  const [selectedIndicators, setSelectedIndicators] = useState<Array<{
    indicator_name: string;
    params?: Record<string, any>;
  }>>([]);
  
  // Fetch snippets when showSnippets is true
  useEffect(() => {
    if (!showSnippets) return;
    
    const fetchSnippets = async () => {
      setLoadingSnippets(true);
      try {
        const response = await snippetApi.list({ is_active: true });
        setSnippets(response.results || []);
      } catch (error) {
        console.error('Failed to fetch snippets:', error);
      } finally {
        setLoadingSnippets(false);
      }
    };
    
    fetchSnippets();
  }, [showSnippets]);

  // Extract variables from prompt text
  useEffect(() => {
    const variablePattern = /\{\{([^}]+)\}\}/g;
    const matches = [...value.matchAll(variablePattern)];
    const variables = matches.map(match => match[1].trim());
    const uniqueVariables = Array.from(new Set(variables));
    setDetectedVariables(uniqueVariables);
  }, [value]);

  // Store monaco instance for theme switching
  const monacoRef = useRef<any>(null);

  // Switch theme when isDark changes
  useEffect(() => {
    if (monacoRef.current) {
      monacoRef.current.editor.setTheme(isDark ? 'prompt-theme-dark' : 'prompt-theme-light');
    }
  }, [isDark]);

  // Configure Monaco before mount - define themes
  const handleEditorWillMount = (monaco: any) => {
    monacoRef.current = monaco;

    // Register custom language for prompt templates (only once)
    if (!monaco.languages.getLanguages().some((lang: any) => lang.id === 'prompt-template')) {
      monaco.languages.register({ id: 'prompt-template' });

      // Define tokens for syntax highlighting
      monaco.languages.setMonarchTokensProvider('prompt-template', {
        tokenizer: {
          root: [
            [/\{\{[^}]+\}\}/, 'variable'],
            [/"(?:[^"\\]|\\.)*"/, 'string'],
            [/'(?:[^'\\]|\\.)*'/, 'string'],
          ],
        },
      });
    }

    // Define light theme for prompt templates
    monaco.editor.defineTheme('prompt-theme-light', {
      base: 'vs',
      inherit: true,
      rules: [
        { token: 'variable', foreground: '0066CC', fontStyle: 'bold' },
        { token: 'string', foreground: '008000' },
      ],
      colors: {},
    });

    // Define dark theme for prompt templates
    monaco.editor.defineTheme('prompt-theme-dark', {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'variable', foreground: '6CB6FF', fontStyle: 'bold' },
        { token: 'string', foreground: '89D185' },
      ],
      colors: {},
    });
  };

  // Configure Monaco Editor after mount
  const handleEditorDidMount = (editor: any, monaco: any) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    
    // Expose editor ref to parent if provided
    if (externalEditorRef) {
      (externalEditorRef as React.MutableRefObject<any>).current = editor;
    }

    // Add variable completion suggestions
    monaco.languages.registerCompletionItemProvider('prompt-template', {
      provideCompletionItems: (_model: any, position: any) => {
        const suggestions = availableInputs.map(input => ({
          label: input,
          kind: monaco.languages.CompletionItemKind.Variable,
          insertText: `{{${input}}}`,
          documentation: `Insert ${input} variable`,
          range: {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: position.column,
            endColumn: position.column,
          },
        }));


        return { suggestions };
      },
    });

    // Enable drag-and-drop
    editor.updateOptions({
      dragAndDrop: true,
    });
  };

  // Handle drag start
  const handleDragStart = (e: React.DragEvent, input: InputDefinition) => {
    e.dataTransfer.setData('inputName', input.name);
    e.dataTransfer.effectAllowed = 'copy';
    setIsDragging(true);
  };

  // Handle drag end
  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Handle drop into editor
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const inputName = e.dataTransfer.getData('inputName');
    const snippetText = e.dataTransfer.getData('snippetText');
    
    if ((inputName || snippetText) && editorRef.current) {
      const editor = editorRef.current;
      const position = editor.getPosition();
      const range = {
        startLineNumber: position.lineNumber,
        startColumn: position.column,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      };

      const text = inputName ? `{{${inputName}}}` : snippetText;

      editor.executeEdits('', [
        {
          range,
          text: text,
          forceMoveMarkers: true,
        },
      ]);

      editor.focus();
    }
    setIsDragging(false);
  };

  // Handle drag over
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  // Insert input at cursor position
  const insertInput = (inputName: string) => {
    if (editorRef.current) {
      const editor = editorRef.current;
      const position = editor.getPosition();
      const range = {
        startLineNumber: position.lineNumber,
        startColumn: position.column,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      };

      editor.executeEdits('', [
        {
          range,
          text: `{{${inputName}}}`,
          forceMoveMarkers: true,
        },
      ]);

      editor.focus();
    }
  };

  // Insert snippet at cursor position (public utility for parent components)
  const insertSnippet = (snippetName: string) => {
    if (editorRef.current) {
      const editor = editorRef.current;
      const position = editor.getPosition();
      const snippetText = `[[snippet:${snippetName}]]`;
      
      const range = {
        startLineNumber: position.lineNumber,
        startColumn: position.column,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      };

      editor.executeEdits('', [
        {
          range,
          text: snippetText,
          forceMoveMarkers: true,
        },
      ]);

      // Set cursor position after the inserted text
      const newPosition = {
        lineNumber: position.lineNumber,
        column: position.column + snippetText.length,
      };
      editor.setPosition(newPosition);

      // Update the value through onChange
      onChange(editor.getValue());
      editor.focus();
    }
  };

  // Expose insertSnippet method via ref
  useEffect(() => {
    if (externalEditorRef && editorRef.current) {
      (externalEditorRef as any).insertSnippet = insertSnippet;
    }
  }, [externalEditorRef, editorRef.current]);

  // Remove input
  const handleRemoveInput = (inputName: string) => {
    const updatedInputs = availableInputs.filter(input => input.name !== inputName);
    onInputsChange?.(updatedInputs);
  };

  // Edit input
  const handleEditInput = (input: InputDefinition) => {
    console.log('🔵 [PromptEditor] Loading input for edit:', input);
    setEditingInput(input);
    setIsEditMode(true);
    setNewInputName(input.name);
    setNewInputType(input.type);
    setNewInputDescription(input.description || '');
    
    // Load all dynamic fields from the input
    const fields: Record<string, any> = {};
    if (input.expert_id !== undefined) fields.expert_id = input.expert_id;
    if (input.expert_name) fields.expert_name = input.expert_name;
    if (input.indicator_name) fields.indicator_name = input.indicator_name;
    if (input.symbol) fields.symbol = input.symbol;
    if (input.timeframe) fields.timeframe = input.timeframe;
    if (input.data_type) fields.data_type = input.data_type;
    if (input.source) fields.source = input.source;
    
    // Account data, positions, journal: handle both account_ids (new) and account_id (legacy)
    if (input.account_ids) {
      fields.account_ids = input.account_ids;
    } else if (input.account_id) {
      // For account_data type, convert legacy single account_id to array
      if (input.type === 'account_data' || input.type === 'open_positions' || input.type === 'trading_journal') {
        fields.account_ids = [input.account_id];
      } else {
        // For indicator, price_data, etc., keep as single account_id
        fields.account_id = input.account_id;
        console.log('🔵 [PromptEditor] Loading account_id for', input.type, ':', input.account_id);
      }
    }
    
    // Clean up: if this is indicator/price_data type but has account_ids, convert it to account_id
    if ((input.type === 'indicator' || input.type === 'price_data') && input.account_ids && !input.account_id) {
      // Take first account from array
      fields.account_id = input.account_ids[0];
      delete fields.account_ids;
      console.log('🔵 [PromptEditor] Converted account_ids to account_id:', fields.account_id);
    }
    
    if (input.count !== undefined) fields.count = input.count;
    if (input.currency) fields.currency = input.currency;
    if (input.impact) fields.impact = input.impact;
    
    // Handle impacts (multi) vs impact (single legacy)
    if (input.impacts) {
      fields.impacts = [...input.impacts];
    } else if (input.impact && input.type === 'economic_calendar') {
      fields.impacts = [input.impact];
    } else if (input.type === 'economic_calendar') {
      fields.impacts = [];
    }

    // Handle symbols (multi) vs symbol (single legacy)
    if (input.symbols) {
      fields.symbols = [...input.symbols];
    } else if (input.symbol && (input.type === 'economic_calendar' || input.type === 'cot_data')) {
      fields.symbols = [input.symbol];
    } else if (input.type === 'economic_calendar' || input.type === 'cot_data') {
      fields.symbols = [];
    }
    if (input.duration_days !== undefined) fields.duration_days = input.duration_days; // Duration filter (economic_calendar, news_data, cot_data)
    if (input.tags) fields.tags = [...input.tags]; // News topic tags
    if (input.filters) fields.filters = [...input.filters];
    if (input.sort) fields.sort = { ...input.sort };
    if (input.asset_class !== undefined) fields.asset_class = input.asset_class;
    if (input.page !== undefined) fields.page = input.page;
    if (input.page_size !== undefined) fields.page_size = input.page_size;
    if (input.entry_id) fields.entry_id = input.entry_id;
    if (input.label) fields.label = input.label;
    if (input.text) fields.text = input.text;
    
    // Handle economic_indicators type
    if (input.type === 'economic_indicators') {
      fields.currencies = input.currencies ? [...input.currencies] : [];
      fields.include_comparison = input.include_comparison ?? true;
      // economic_indicator_names are string names like 'GDP', 'CPI'
      if (input.economic_indicator_names && Array.isArray(input.economic_indicator_names)) {
        fields.economic_indicator_names = [...input.economic_indicator_names];
      } else {
        fields.economic_indicator_names = [];
      }
    }
    
    // Load indicator set fields
    if (input.use_indicator_set !== undefined) fields.use_indicator_set = input.use_indicator_set;
    if (input.indicator_set_id !== undefined) fields.indicator_set_id = input.indicator_set_id;
    
    // Load indicators (only for manual indicator configuration - not economic_indicators type)
    if (input.type !== 'economic_indicators' && input.indicators) {
      fields.indicators = [...input.indicators];
      setSelectedIndicators([...input.indicators]);
    } else if (input.type !== 'economic_indicators') {
      setSelectedIndicators([]);
    }
    
    console.log('🔵 [PromptEditor] Setting dynamicFields:', fields);
    setDynamicFields(fields);
    setCustomInputDialogOpen(true);
  };

  // Copy/Clone input
  const handleCopyInput = (input: InputDefinition) => {
    setEditingInput(null);
    setIsEditMode(false);
    
    // Generate a unique name for the copy
    let copyName = `${input.name}_copy`;
    let counter = 1;
    while (availableInputs.some(inp => inp.name === copyName)) {
      copyName = `${input.name}_copy${counter}`;
      counter++;
    }
    
    setNewInputName(copyName);
    setNewInputType(input.type);
    setNewInputDescription(input.description ? `${input.description} (copy)` : '');
    
    // Load all dynamic fields from the input
    const fields: Record<string, any> = {};
    if (input.expert_id !== undefined) fields.expert_id = input.expert_id;
    if (input.expert_name) fields.expert_name = input.expert_name;
    if (input.indicator_name) fields.indicator_name = input.indicator_name;
    if (input.symbol) fields.symbol = input.symbol;
    if (input.timeframe) fields.timeframe = input.timeframe;
    if (input.data_type) fields.data_type = input.data_type;
    if (input.source) fields.source = input.source;
    
    // Account data, positions, journal: handle both account_ids (new) and account_id (legacy)
    if (input.account_ids) {
      fields.account_ids = [...input.account_ids]; // Clone the array
    } else if (input.account_id) {
      // Convert legacy single account_id to array for backward compatibility
      fields.account_ids = [input.account_id];
    }
    
    if (input.count !== undefined) fields.count = input.count;
    if (input.currency) fields.currency = input.currency;
    if (input.impact) fields.impact = input.impact;
    
    // Handle impacts (multi) vs impact (single legacy)
    if (input.impacts) {
      fields.impacts = [...input.impacts];
    } else if (input.impact && input.type === 'economic_calendar') {
      fields.impacts = [input.impact];
    } else if (input.type === 'economic_calendar') {
      fields.impacts = [];
    }

    // Handle symbols (multi) vs symbol (single legacy)
    if (input.symbols) {
      fields.symbols = [...input.symbols];
    } else if (input.symbol && (input.type === 'economic_calendar' || input.type === 'cot_data')) {
      fields.symbols = [input.symbol];
    } else if (input.type === 'economic_calendar' || input.type === 'cot_data') {
      fields.symbols = [];
    }
    if (input.duration_days !== undefined) fields.duration_days = input.duration_days; // Duration filter (economic_calendar, news_data, cot_data)
    if (input.tags) fields.tags = [...input.tags]; // News topic tags
    if (input.filters) fields.filters = [...input.filters];
    if (input.sort) fields.sort = { ...input.sort };
    if (input.asset_class !== undefined) fields.asset_class = input.asset_class;
    if (input.page !== undefined) fields.page = input.page;
    if (input.page_size !== undefined) fields.page_size = input.page_size;
    if (input.entry_id) fields.entry_id = input.entry_id;
    if (input.label) fields.label = input.label;
    if (input.text) fields.text = input.text;
    
    // Handle economic_indicators type for copy
    if (input.type === 'economic_indicators') {
      fields.currencies = input.currencies ? [...input.currencies] : [];
      fields.include_comparison = input.include_comparison ?? true;
      // economic_indicator_names are string names like 'GDP', 'CPI'
      if (input.economic_indicator_names && Array.isArray(input.economic_indicator_names)) {
        fields.economic_indicator_names = [...input.economic_indicator_names];
      } else {
        fields.economic_indicator_names = [];
      }
    }
    
    // Load indicator set fields
    if (input.use_indicator_set !== undefined) fields.use_indicator_set = input.use_indicator_set;
    if (input.indicator_set_id !== undefined) fields.indicator_set_id = input.indicator_set_id;
    
    // Load indicators (only for manual indicator configuration - not economic_indicators type)
    if (input.type !== 'economic_indicators' && input.indicators) {
      fields.indicators = [...input.indicators]; // Clone the array
      setSelectedIndicators([...input.indicators]);
    } else if (input.type !== 'economic_indicators') {
      setSelectedIndicators([]);
    }
    
    setDynamicFields(fields);
    setCustomInputDialogOpen(true);
  };

  // Reset dynamic fields when input type changes
  const handleInputTypeChange = (newType: InputType) => {
    setNewInputType(newType);
    
    // Initialize default values for certain input types
    const defaultFields: Record<string, any> = {};
    
    // Types that require symbol and timeframe
    if (newType === 'indicator' || newType === 'price_data') {
      defaultFields.symbol = '';
      defaultFields.timeframe = 'H1';
      defaultFields.count = 100;
    }
    if (newType === 'chart_image' || newType === 'chart_image_file') {
      defaultFields.symbol = '';
      defaultFields.timeframe = 'H1';
      defaultFields.count = 100;
    }

    // Initialize array fields to empty arrays for types that use them
    else if (newType === 'economic_calendar') {
      defaultFields.symbols = [];
      defaultFields.impacts = [];
      defaultFields.count = 10;
      defaultFields.duration_days = 30;
    } else if (newType === 'cot_data') {
      defaultFields.symbols = [];
      defaultFields.count = 10;
    } else if (newType === 'news_data') {
      defaultFields.count = 10;
      defaultFields.tags = [];
    } else if (newType === 'account_data' || newType === 'open_positions' || newType === 'trading_journal') {
      defaultFields.account_ids = [];
    } else if (newType === 'previous_expert_results') {
      defaultFields.count = 10;
    } else if (newType === 'options_data') {
      defaultFields.symbol = '';
      defaultFields.option_type = 'both';
      defaultFields.near_the_money = 10;
      defaultFields.max_expirations = 3;
      defaultFields.include_greeks = true;
    } else if (newType === 'economic_indicators') {
      defaultFields.currencies = [];
      defaultFields.economic_indicator_names = [];
      defaultFields.count = 10;
      defaultFields.include_comparison = true;
    } else if (newType === 'screener_snapshot') {
      defaultFields.timeframe = 'D1';
      defaultFields.filters = [];
      defaultFields.sort = { field: 'symbol', direction: 'asc' };
      defaultFields.asset_class = '';
      defaultFields.page = 1;
      defaultFields.count = 20;
    }
    // OpenBB Input Types
    else if (newType === 'stock_fundamentals') {
      defaultFields.symbol = '';
      defaultFields.data_types = ['profile', 'ratios'];
      defaultFields.period = 'annual';
      defaultFields.limit = 4;
      defaultFields.provider = '';  // Auto-select
      defaultFields.include_estimates = false;
    } else if (newType === 'openbb_news') {
      defaultFields.symbols = [];
      defaultFields.limit = 20;
      defaultFields.source_type = 'world';
      defaultFields.provider = '';  // Auto-select
      defaultFields.include_content = false;
      defaultFields.start_date = '';
      defaultFields.end_date = '';
    } else if (newType === 'openbb_options') {
      defaultFields.symbol = '';
      defaultFields.option_type = 'both';
      defaultFields.expiration = '';  // All expirations
      defaultFields.provider = '';
      defaultFields.include_unusual = false;
    } else if (newType === 'openbb_forex') {
      defaultFields.pairs = [];
      defaultFields.data_types = ['historical'];
      defaultFields.timeframe = '1d';
      defaultFields.bars_count = 100;
      defaultFields.provider = '';
    } else if (newType === 'openbb_crypto') {
      defaultFields.crypto_symbols = [];
      defaultFields.data_types = ['quote'];
      defaultFields.timeframe = '1d';
      defaultFields.bars_count = 100;
      defaultFields.provider = '';
    } else if (newType === 'openbb_etf') {
      defaultFields.symbol = '';
      defaultFields.data_types = ['info', 'holdings'];
      defaultFields.holdings_limit = 20;
      defaultFields.include_sectors = true;
      defaultFields.include_countries = false;
      defaultFields.provider = '';
    } else if (newType === 'openbb_economic') {
      defaultFields.countries = ['US'];
      defaultFields.economic_indicators = ['gdp', 'cpi', 'interest_rate'];
      defaultFields.limit = 10;
      defaultFields.include_forecast = false;
      defaultFields.include_calendar = false;
      defaultFields.fred_series = [];
      defaultFields.provider = '';
    }
    // Knowledge Graph
    else if (newType === 'knowledge_graph') {
      defaultFields.query_type = 'currency_context';
      defaultFields.currency = 'USD';
      defaultFields.days_back = 7;
      defaultFields.limit = 30;
    }
    // Storage
    else if (newType === 'storage_text') {
      defaultFields.storage_name = '';
      defaultFields.storage_tags = [];
      defaultFields.match_all_tags = false;
      defaultFields.include_metadata = false;
    } else if (newType === 'storage_file') {
      defaultFields.storage_name = '';
      defaultFields.storage_tags = [];
      defaultFields.extract_text = true;
    }
    // Global Storage
    else if (newType === 'global_storage_text') {
      defaultFields.storage_name = '';
      defaultFields.storage_tags = [];
      defaultFields.match_all_tags = false;
      defaultFields.include_metadata = false;
    } else if (newType === 'global_storage_file') {
      defaultFields.storage_name = '';
      defaultFields.storage_tags = [];
      defaultFields.extract_text = true;
    }
    
    setDynamicFields(defaultFields);
  };

  useEffect(() => {
    if (!customInputDialogOpen || isEditMode || availableInputTypesLoading) {
      return;
    }
    if (visibleInputTypeOptions.length === 0) {
      return;
    }
    if (!visibleInputTypeOptions.some(option => option.value === newInputType)) {
      handleInputTypeChange(visibleInputTypeOptions[0].value);
    }
  }, [
    availableInputTypesLoading,
    customInputDialogOpen,
    handleInputTypeChange,
    isEditMode,
    newInputType,
    visibleInputTypeOptions,
  ]);
  
  // Reset all dialog fields
  const resetDialogFields = () => {
    setNewInputName('');
    setNewInputType('static_text');
    setNewInputDescription('');
    setDynamicFields({});
    setEditingInput(null);
    setIsEditMode(false);
    setSelectedIndicators([]);
  };
  
  
  // Add or update custom input
  const handleAddCustomInput = () => {
    const trimmedName = newInputName.trim();
    console.log('🔵 [PROMPTEDITOR] handleAddCustomInput called, trimmedName:', trimmedName, 'isEditMode:', isEditMode);
    console.log('🔵 [PROMPTEDITOR] Current dynamicFields:', dynamicFields);
    console.log('🔵 [PROMPTEDITOR] Current selectedIndicators:', selectedIndicators);
    console.log('🔵 [PROMPTEDITOR] Current availableInputs:', availableInputs);
    console.log('🔵 [PROMPTEDITOR] onInputsChange exists?', !!onInputsChange);
    
    // Check for duplicate name (but allow keeping same name when editing)
    const isDuplicate = availableInputs.some(
      input => input.name === trimmedName && (!isEditMode || input.name !== editingInput?.name)
    );
    
    if (trimmedName && !isDuplicate) {
      // Create a clean copy of dynamicFields, removing incorrect account field types
      const cleanedFields = { ...dynamicFields };
      
      // For indicator, chart_image and price_data: remove account_ids (array), keep account_id (single)
      if (newInputType === 'indicator' || newInputType === 'chart_image' || newInputType === 'chart_image_file' || newInputType === 'price_data') {
        delete cleanedFields.account_ids;
      }
      // For account_data, positions, journal: remove account_id (single), keep account_ids (array)
      else if (newInputType === 'account_data' || newInputType === 'open_positions' || newInputType === 'trading_journal') {
        delete cleanedFields.account_id;
      }
      
      // For economic_calendar and cot_data: remove symbol (single), keep symbols (array)
      if (newInputType === 'economic_calendar' || newInputType === 'cot_data') {
        delete cleanedFields.symbol;
      }
      
      // For economic_calendar: remove impact (single), keep impacts (array)
      if (newInputType === 'economic_calendar') {
        delete cleanedFields.impact;
      }
      
      // For options_data: keep symbol (single), remove symbols (array)
      if (newInputType === 'options_data') {
        delete cleanedFields.symbols;
        delete cleanedFields.account_ids;
        delete cleanedFields.account_id;
      }
      
      // For economic_indicators: keep currencies (array), remove symbol/symbols/account fields
      if (newInputType === 'economic_indicators') {
        delete cleanedFields.symbol;
        delete cleanedFields.symbols;
        delete cleanedFields.account_ids;
        delete cleanedFields.account_id;
        // Ensure currencies is an array
        if (!Array.isArray(cleanedFields.currencies)) {
          cleanedFields.currencies = [];
        }
      }
      
      // For indicator and chart_image types, clean up based on use_indicator_set
      if (newInputType === 'indicator' || newInputType === 'chart_image' || newInputType === 'chart_image_file') {
        if (cleanedFields.use_indicator_set) {
          // Using indicator set: remove manual indicators
          delete cleanedFields.indicators;
          console.log('🟣 [PROMPTEDITOR] Using indicator set mode, cleaned fields:', cleanedFields);
        } else {
          // Manual indicators: remove indicator set references
          delete cleanedFields.use_indicator_set;
          delete cleanedFields.indicator_set_id;
          console.log('🟣 [PROMPTEDITOR] Using manual indicators mode, cleaned fields:', cleanedFields);
        }
      }
      
      console.log('🔵 [PROMPTEDITOR] Final cleanedFields before creating inputData:', JSON.stringify(cleanedFields, null, 2));
      console.log('🔵 [PROMPTEDITOR] Input type:', newInputType);
      console.log('🔵 [PROMPTEDITOR] cleanedFields.currencies:', cleanedFields.currencies);
      
      const inputData: InputDefinition = {
        name: trimmedName,
        type: newInputType,
        description: newInputDescription.trim() || undefined,
        ...cleanedFields,
        // Include indicators for indicator and chart_image input types (when NOT using indicator set)
        // Note: This does NOT apply to economic_indicators - those use economic_indicator_names (string array)
        ...((newInputType === 'indicator' || newInputType === 'chart_image' || newInputType === 'chart_image_file') && !cleanedFields.use_indicator_set && selectedIndicators.length > 0
          ? { indicators: selectedIndicators }
          : {}),
      };
      
      // Ensure economic_indicators has currencies properly set
      if (newInputType === 'economic_indicators') {
        inputData.currencies = cleanedFields.currencies || [];
        console.log('🔵 [PROMPTEDITOR] Setting economic_indicators currencies:', inputData.currencies);
      }
      
      console.log('🔵 [PROMPTEDITOR] Saving input with data:', JSON.stringify(inputData, null, 2));
      
      let updatedInputs: InputDefinition[];
      
      if (isEditMode && editingInput) {
        // Update existing input
        updatedInputs = availableInputs.map(input =>
          input.name === editingInput.name ? inputData : input
        );
        console.log('🔵 [PROMPTEDITOR] Updating input:', inputData);
      } else {
        // Add new input
        updatedInputs = [...availableInputs, inputData];
        console.log('🔵 [PROMPTEDITOR] Adding new input:', inputData);
        // Only insert into prompt if NOT a file input type
        if (newInputType !== 'chart_image_file') {
          insertInput(trimmedName);
        }
      }
      
      console.log('🔵 [PROMPTEDITOR] Updated inputs array:', updatedInputs);
      console.log('🔵 [PROMPTEDITOR] Calling onInputsChange...');
      
      onInputsChange?.(updatedInputs);
      resetDialogFields();
      setCustomInputDialogOpen(false);
    } else {
      console.warn('🔵 [PROMPTEDITOR] Input not added/updated - duplicate or empty name');
    }
  };

  // Test input handler - calls API to resolve input and show results
  const handleTestInput = async () => {
    if (!systemId) {
      toast.error('System ID not available - save the expert first');
      return;
    }
    if (!newInputType) {
      toast.error('Please select an input type first');
      return;
    }

    setIsTesting(true);
    try {
      const result = await sharedInputApi.testInput(systemId, newInputType, dynamicFields);
      setTestResult(result);
      setTestResultDialogOpen(true);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to test input');
      setTestResult({
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to test input',
        error_type: 'RequestError',
        execution_time_ms: 0,
      });
      setTestResultDialogOpen(true);
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle2" fontWeight="bold">
          {label}
        </Typography>
        {showInputs && (
        <Box display="flex" gap={1} alignItems="center">
          <Tooltip title="Inputs in prompt">
            <Chip
              size="small"
              label={`${detectedVariables.length} input${detectedVariables.length !== 1 ? 's' : ''}`}
              color="primary"
              variant="outlined"
            />
          </Tooltip>
          <Tooltip title="Add custom input">
            <IconButton size="small" onClick={() => setCustomInputDialogOpen(true)}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        )}
      </Box>

      {helperText && (
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          {helperText}
        </Typography>
      )}

      {/* Mobile toggle for inputs panel */}
      {showInputs && isMobile && (availableInputs.length > 0 || hideEditor) && !hideEditor && (
        <Button
          size="small"
          variant={showInputsPanel ? 'contained' : 'outlined'}
          onClick={() => setShowInputsPanel(!showInputsPanel)}
          sx={{ mb: 1, alignSelf: 'flex-start' }}
        >
          {showInputsPanel ? 'Hide Inputs' : `Show Inputs (${availableInputs.length})`}
        </Button>
      )}

      <Box display="flex" gap={2} sx={{ overflow: 'hidden', flexDirection: isMobile ? 'column' : 'row' }}>
        {/* Available Inputs Sidebar - Always show in hideEditor mode (CODE experts) */}
        {showInputs && (availableInputs.length > 0 || hideEditor) && (
          <Collapse in={isMobile ? (showInputsPanel || hideEditor) : true} sx={{ width: isMobile ? '100%' : 'auto' }}>
          <Paper
            sx={{
              width: hideEditor ? '100%' : (isMobile ? '100%' : 220),
              flexShrink: hideEditor ? 1 : 0,
              p: 1.5,
              bgcolor: isDark ? 'grey.900' : 'grey.50',
              border: 1,
              borderColor: isDark ? 'grey.700' : 'grey.300',
              ...(isMobile && !hideEditor && {
                maxHeight: 300,
                overflowY: 'auto',
              }),
              ...(!isMobile && !hideEditor && {
                // Align sidebar height with the editor (editor toolbar adds ~40px on top)
                maxHeight: editorHeight + 40,
                overflowY: 'auto',
                alignSelf: 'flex-start',
              }),
            }}
          >
            <Typography variant="caption" fontWeight="bold" color="text.secondary" gutterBottom display="block">
              AVAILABLE INPUTS
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              {hideEditor ? 'These variables will be available in your Python code' : 'Drag into editor →'}
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Stack spacing={0.5}>
              {availableInputs.map((input) => (
                <Paper
                  key={input.name}
                  draggable={input.type !== 'chart_image_file'}
                  onDragStart={(e: React.DragEvent) => {
                    if (input.type === 'chart_image_file') {
                      e.preventDefault();
                      return;
                    }
                    handleDragStart(e, input);
                  }}
                  onDragEnd={handleDragEnd}
                  sx={{
                    p: 1,
                    cursor: input.type === 'chart_image_file' ? 'default' : 'grab',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: isDark ? 'grey.700' : 'grey.300',
                    '&:hover': {
                      bgcolor: isDark ? 'action.hover' : 'primary.50',
                      borderColor: 'primary.main',
                      transform: input.type === 'chart_image_file' ? 'none' : 'translateX(2px)',
                    },
                    '&:active': {
                      cursor: input.type === 'chart_image_file' ? 'default' : 'grabbing',
                    },
                    transition: 'all 0.2s',
                  }}
                  onClick={() => {
                    if (input.type !== 'chart_image_file') {
                      insertInput(input.name);
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Box display="flex" alignItems="center" gap={0.5} flex={1} minWidth={0}>
                      <DragIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
                      <Tooltip title={input.name} placement="top">
                        <Typography 
                          variant="caption" 
                          fontFamily="monospace" 
                          fontWeight="bold"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          {input.name}
                        </Typography>
                      </Tooltip>
                    </Box>
                    <Box display="flex" gap={0.25} flexShrink={0}>
                      <Tooltip title="Copy input">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyInput(input);
                          }}
                          sx={{ p: 0.25 }}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit input">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditInput(input);
                          }}
                          sx={{ p: 0.25 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove input">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveInput(input.name);
                          }}
                          sx={{ p: 0.25 }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Chip 
                      label={INPUT_TYPE_LABELS[input.type]} 
                      size="small" 
                      color={INPUT_TYPE_COLORS[input.type]}
                      sx={{ fontSize: '0.65rem', height: '18px' }}
                    />
                  </Box>
                  {input.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                      {input.description}
                    </Typography>
                  )}
                  {/* Display key configuration fields */}
                  {input.type === 'expert' && input.expert_id && (
                    <Typography variant="caption" color="primary" sx={{ fontSize: '0.65rem' }}>
                      Expert ID: {input.expert_id}
                    </Typography>
                  )}
                  {input.type === 'indicator' && (
                    <>
                      <Typography variant="caption" color="secondary" sx={{ fontSize: '0.65rem' }}>
                        {input.symbol} • {input.timeframe} • {input.count || 100} bars
                      </Typography>
                      {input.indicators && input.indicators.length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          {input.indicators.length} indicator(s): {input.indicators.map(i => i.indicator_name).join(', ')}
                        </Typography>
                      )}
                    </>
                  )}
                  {(input.type === 'chart_image' || input.type === 'chart_image_file') && (
                    <>
                      <Typography variant="caption" color="secondary" sx={{ fontSize: '0.65rem' }}>
                        {input.symbol} • {input.timeframe} • {input.width || 800}x{input.height || 600}
                      </Typography>
                      {input.use_indicator_set && input.indicator_set_id && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          Using Indicator Set ID: {input.indicator_set_id}
                        </Typography>
                      )}
                      {input.indicators && input.indicators.length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          {input.indicators.length} indicator(s): {input.indicators.map(i => i.indicator_name).join(', ')}
                        </Typography>
                      )}
                    </>
                  )}
                  {input.type === 'price_data' && (
                    <Typography variant="caption" color="info.main" sx={{ fontSize: '0.65rem' }}>
                      {input.symbol} • {input.timeframe}
                    </Typography>
                  )}
                  {input.type === 'account_data' && (input.account_ids || input.account_id) && (
                    <Typography variant="caption" color="success.main" sx={{ fontSize: '0.65rem' }}>
                      {input.account_ids 
                        ? `${input.account_ids.length} account(s) selected`
                        : `Account: ${input.account_id}`}
                    </Typography>
                  )}
                  {input.type === 'open_positions' && (input.account_ids || input.account_id) && (
                    <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.65rem' }}>
                      {input.account_ids 
                        ? `${input.account_ids.length} account(s)${input.count ? ` • Last ${input.count}` : ''}`
                        : `Account: ${input.account_id}`}
                    </Typography>
                  )}
                  {input.type === 'trading_journal' && (input.account_ids || input.account_id) && (
                    <Typography variant="caption" color="default" sx={{ fontSize: '0.65rem' }}>
                      {input.account_ids 
                        ? `${input.account_ids.length} account(s)${input.count ? ` • Last ${input.count}` : ''}`
                        : `Account: ${input.account_id}`}
                    </Typography>
                  )}
                  {input.type === 'symbol_data' && (
                    <Typography variant="caption" color="error.main" sx={{ fontSize: '0.65rem' }}>
                      Symbol: {input.symbol}
                    </Typography>
                  )}
                  {input.type === 'economic_calendar' && (
                    <Typography variant="caption" color="info.main" sx={{ fontSize: '0.65rem' }}>
                      {(input.symbols && input.symbols.length > 0) ? `${input.symbols.join(', ')} • ` : 
                       (input.symbol ? `${input.symbol} • ` : '')}
                      {(input.impacts && input.impacts.length > 0) ? `Impact: ${input.impacts.join(', ')} • ` : 
                       (input.impact ? `Impact: ${input.impact} • ` : '')}
                      Count: {input.count || 10}
                    </Typography>
                  )}
                  {input.type === 'news_data' && (
                    <Typography variant="caption" color="info.main" sx={{ fontSize: '0.65rem' }}>
                      Count: {input.count || 10}
                      {input.tags && input.tags.length > 0 ? ` • Tags: ${input.tags.join(', ')}` : ''}
                      {input.source ? ` • Source: ${input.source}` : ''}
                    </Typography>
                  )}
                  {input.type === 'cot_data' && (
                    <Typography variant="caption" color="info.main" sx={{ fontSize: '0.65rem' }}>
                      {(input.symbols && input.symbols.length > 0) ? `${input.symbols.join(', ')} • ` : 
                       (input.symbol ? `${input.symbol} • ` : '')}
                      Count: {input.count || 10}
                    </Typography>
                  )}
                  {input.type === 'options_data' && (
                    <Typography variant="caption" color="info.main" sx={{ fontSize: '0.65rem' }}>
                      {input.symbol} • {input.option_type || 'both'} • {input.near_the_money || 10} strikes
                    </Typography>
                  )}
                  {input.type === 'economic_indicators' && (
                    <Typography variant="caption" color="info.main" sx={{ fontSize: '0.65rem' }}>
                      {(input.currencies && input.currencies.length > 0) 
                        ? input.currencies.join(', ') 
                        : 'No currencies'} • {input.count || 10} pts
                    </Typography>
                  )}
                  {input.type === 'screener_snapshot' && (
                    <Typography variant="caption" color="info.main" sx={{ fontSize: '0.65rem' }}>
                      {input.timeframe || 'D1'} | {input.asset_class || 'All assets'} | {(input.filters?.length || 0)} filter(s) | Count: {input.count || input.page_size || 20}
                    </Typography>
                  )}
                  {input.type === 'previous_expert_results' && input.expert_name && (
                    <Typography variant="caption" color="primary" sx={{ fontSize: '0.65rem' }}>
                      Expert: {input.expert_name} • Last {input.count || 10} results
                    </Typography>
                  )}
                  {input.type === 'current_time' && (
                    <Typography variant="caption" color="secondary" sx={{ fontSize: '0.65rem' }}>
                      Auto-injects current UTC time
                    </Typography>
                  )}
                  {/* OpenBB Input Type Displays */}
                  {input.type === 'stock_fundamentals' && (
                    <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.65rem' }}>
                      {input.symbol} • {(input as any).data_types?.join(', ') || 'profile, ratios'}
                    </Typography>
                  )}
                  {input.type === 'openbb_news' && (
                    <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.65rem' }}>
                      {(input as any).symbols?.length > 0 ? (input as any).symbols.join(', ') : 'All news'} • {(input as any).limit || 20} articles
                    </Typography>
                  )}
                  {input.type === 'openbb_options' && (
                    <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.65rem' }}>
                      {input.symbol} • {(input as any).option_type || 'both'} • {(input as any).strikes_count || 10} strikes
                    </Typography>
                  )}
                  {input.type === 'openbb_forex' && (
                    <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.65rem' }}>
                      {input.symbol} • {(input as any).data_types?.join(', ') || 'quote'}
                    </Typography>
                  )}
                  {input.type === 'openbb_crypto' && (
                    <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.65rem' }}>
                      {input.symbol} • {(input as any).data_types?.join(', ') || 'price'}
                    </Typography>
                  )}
                  {input.type === 'openbb_etf' && (
                    <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.65rem' }}>
                      {input.symbol} • {(input as any).data_types?.join(', ') || 'info, holdings'}
                    </Typography>
                  )}
                  {input.type === 'openbb_economic' && (
                    <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.65rem' }}>
                      {(input as any).countries?.join(', ') || 'US'} • {(input as any).indicators?.join(', ') || 'gdp, cpi'}
                    </Typography>
                  )}
                </Paper>
              ))}
            </Stack>

            {/* Shared Inputs Section */}
            {sharedInputs.length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" fontWeight="bold" color="info.main" gutterBottom display="block">
                  SHARED INPUTS
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  System-level inputs (read-only)
                </Typography>
                <Stack spacing={0.5}>
                  {sharedInputs.map((input) => (
                    <Paper
                      key={`shared-${input.name}`}
                      sx={{
                        p: 1,
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        bgcolor: isDark ? 'info.900' : 'info.50',
                        border: 1,
                        borderColor: isDark ? 'info.700' : 'info.200',
                        '&:hover': {
                          bgcolor: isDark ? 'info.800' : 'info.100',
                          borderColor: 'info.main',
                          transform: 'translateX(2px)',
                        },
                        transition: 'all 0.2s',
                      }}
                      onClick={() => {
                        const variable = `{{ shared.${input.name} }}`;
                        const editor = editorRef.current;
                        if (editor) {
                          const selection = editor.getSelection();
                          const id = { major: 1, minor: 1 };
                          const op = { identifier: id, range: selection, text: variable, forceMoveMarkers: true };
                          editor.executeEdits("prompt-editor", [op]);
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                        <Box display="flex" alignItems="center" gap={0.5} flex={1} minWidth={0}>
                          <DragIcon fontSize="small" sx={{ color: 'info.main', flexShrink: 0 }} />
                          <Tooltip title={`shared.${input.name}`} placement="top">
                            <Typography 
                              variant="caption" 
                              fontFamily="monospace" 
                              fontWeight="bold"
                              color={isDark ? 'info.light' : 'info.dark'}
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                                minWidth: 0,
                              }}
                            >
                              shared.{input.name}
                            </Typography>
                          </Tooltip>
                        </Box>
                        <Tooltip title="Copy variable">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(`{{ shared.${input.name} }}`);
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Chip 
                        label={INPUT_TYPE_LABELS[input.input_type as InputType] || input.input_type} 
                        size="small" 
                        color="info"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: '18px' }}
                      />
                      {input.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          {input.description}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Stack>
              </>
            )}
          </Paper>
          </Collapse>
        )}

        {/* Available Snippets */}
        {showSnippets && showSnippetPanel && snippets.length > 0 && (
          <Paper
            sx={{
              width: isMobile ? '100%' : 220,
              flexShrink: 0,
              p: 1.5,
              bgcolor: isDark ? 'success.900' : 'success.50',
              border: 1,
              borderColor: isDark ? 'success.700' : 'success.300',
              ...(!isMobile && {
                maxHeight: editorHeight + 40,
                overflowY: 'auto',
                alignSelf: 'flex-start',
              }),
            }}
          >
            <Typography variant="caption" fontWeight="bold" color={isDark ? 'success.light' : 'success.dark'} gutterBottom display="block">
              AVAILABLE SNIPPETS
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Click or drag into editor →
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Stack spacing={0.5} sx={{ maxHeight: 400, overflowY: 'auto' }}>
              {snippets.map((snippet) => (
                <Paper
                  key={snippet.id}
                  draggable
                  onDragStart={(e: React.DragEvent) => {
                    e.dataTransfer.setData('snippetText', `[[snippet:${snippet.name}]]`);
                    e.dataTransfer.effectAllowed = 'copy';
                  }}
                  sx={{
                    p: 1,
                    cursor: 'grab',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: isDark ? 'success.700' : 'success.200',
                    '&:hover': {
                      bgcolor: isDark ? 'action.hover' : 'success.50',
                      borderColor: 'success.main',
                      transform: 'translateX(2px)',
                    },
                    '&:active': {
                      cursor: 'grabbing',
                    },
                    transition: 'all 0.2s',
                  }}
                  onClick={() => {
                    const editor = editorRef.current;
                    if (!editor) return;
                    const selection = editor.getSelection();
                    const text = `[[snippet:${snippet.name}]]`;
                    editor.executeEdits('', [{
                      range: selection,
                      text: text,
                    }]);
                    onChange(editor.getValue());
                  }}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <DragIcon fontSize="small" sx={{ color: 'success.main', flexShrink: 0 }} />
                    <Tooltip title={snippet.name} placement="top">
                      <Typography 
                        variant="caption" 
                        fontFamily="monospace" 
                        fontWeight="bold"
                        color="success.dark"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                        }}
                      >
                        {snippet.name}
                      </Typography>
                    </Tooltip>
                  </Box>
                  {snippet.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                      {snippet.description}
                    </Typography>
                  )}
                  {snippet.category && (
                    <Chip 
                      label={snippet.category} 
                      size="small" 
                      color="success"
                      sx={{ fontSize: '0.6rem', height: 16 }}
                    />
                  )}
                  {snippet.usage_count > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                      Used {snippet.usage_count}× 
                    </Typography>
                  )}
                </Paper>
              ))}
            </Stack>
          </Paper>
        )}

        {/* Editor - Hidden when hideEditor is true (CODE experts only need inputs) */}
        {!hideEditor && (
        <Box flex={1} minWidth={0} sx={{ overflow: 'hidden' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} flexWrap="wrap" gap={0.5}>
            {!isMobile && (
            <Typography variant="caption" color="text.secondary">
              Editor Height: {editorHeight}px
            </Typography>
            )}
            <Stack direction="row" spacing={0.5} sx={{ ml: 'auto' }}>
              <Tooltip title="Decrease height">
                <IconButton 
                  size="small" 
                  onClick={() => setEditorHeight(prev => Math.max(200, prev - 50))}
                  disabled={disabled}
                >
                  <CollapseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Increase height">
                <IconButton 
                  size="small" 
                  onClick={() => setEditorHeight(prev => Math.min(1000, prev + 50))}
                  disabled={disabled}
                >
                  <ExpandIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {showSnippets && snippets.length > 0 && (
                <Button
                  size="small"
                  variant={showSnippetPanel ? "contained" : "outlined"}
                  color="success"
                  onClick={() => setShowSnippetPanel(!showSnippetPanel)}
                  disabled={disabled}
                  startIcon={<InfoIcon />}
                  sx={{ ml: 1 }}
                >
                  {showSnippetPanel ? "Hide Snippets" : "📋 Browse Snippets"}
                </Button>
              )}
              <Tooltip title="Fullscreen editor">
                <IconButton 
                  size="small" 
                  onClick={() => setIsFullscreen(true)}
                  disabled={disabled}
                >
                  <FullscreenIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
          <Paper
            sx={{
              border: 2,
              borderColor: isDragging ? 'primary.main' : (isDark ? 'grey.700' : 'grey.300'),
              borderRadius: 1,
              overflow: 'hidden',
              transition: 'border-color 0.2s',
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <Editor
              height={editorHeight}
              defaultLanguage="prompt-template"
              theme={isDark ? 'prompt-theme-dark' : 'prompt-theme-light'}
              value={value}
              onChange={(val) => onChange(val || '')}
              beforeMount={handleEditorWillMount}
              onMount={handleEditorDidMount}
              options={{
                minimap: { enabled: false },
                lineNumbers: isMobile ? 'off' : 'on',
                scrollBeyondLastLine: false,
                wordWrap: 'on',
                wrappingStrategy: 'advanced',
                fontSize: isMobile ? 13 : 14,
                fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
                readOnly: disabled,
                padding: { top: 10, bottom: 10 },
                suggestOnTriggerCharacters: true,
                quickSuggestions: true,
                tabCompletion: 'on',
                folding: !isMobile,
                glyphMargin: !isMobile,
              }}
              loading={<Box p={2}>Loading editor...</Box>}
            />
          </Paper>

          {/* Detected Inputs Display */}
          {detectedVariables.length > 0 && (
            <Box mt={1}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                Inputs used in prompt:
              </Typography>
              <Box display="flex" gap={0.5} flexWrap="wrap">
                {detectedVariables.map((variable) => {
                  // Check if this is a shared input (shared.xxx)
                  const isSharedInput = variable.startsWith('shared.');
                  const sharedInputName = isSharedInput ? variable.replace('shared.', '') : null;
                  const sharedInput = sharedInputName ? sharedInputs.find(inp => inp.name === sharedInputName) : null;
                  const input = availableInputs.find(inp => inp.name === variable);
                  
                  // Determine if input is defined (either as regular input or shared input)
                  const isDefined = input || sharedInput;
                  const typeLabel = input 
                    ? INPUT_TYPE_LABELS[input.type] 
                    : sharedInput 
                      ? INPUT_TYPE_LABELS[sharedInput.input_type as InputType] || sharedInput.input_type
                      : null;
                  
                  return (
                    <Chip
                      key={variable}
                      label={isDefined ? `${variable} (${typeLabel})` : variable}
                      size="small"
                      color={isDefined ? (sharedInput ? 'info' : 'success') : 'warning'}
                      variant="outlined"
                      icon={isDefined ? undefined : <InfoIcon />}
                    />
                  );
                })}
              </Box>
              {detectedVariables.some(v => {
                // Check if variable is defined in either availableInputs or sharedInputs
                const isSharedInput = v.startsWith('shared.');
                const sharedInputName = isSharedInput ? v.replace('shared.', '') : null;
                const sharedInput = sharedInputName ? sharedInputs.find(inp => inp.name === sharedInputName) : null;
                const input = availableInputs.find(inp => inp.name === v);
                return !input && !sharedInput;
              }) && (
                <Alert severity="warning" sx={{ mt: 1 }} icon={<InfoIcon />}>
                  <Typography variant="caption">
                    Some inputs are not defined. Add them using the [+] button in the Inputs sidebar.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}

          {/* Help Text */}
          <Alert severity="info" sx={{ mt: 2 }} icon={<InfoIcon />}>
            <Typography variant="caption">
              <strong>Tips:</strong>
              <br />• Drag inputs from the sidebar into the editor
              <br />• Click an input to insert it at cursor position
              <br />• Use <code>{'{{variable}}'}</code> syntax for dynamic values
              <br />• Type <code>{'{'}</code> for autocomplete suggestions
            </Typography>
          </Alert>
        </Box>
        )}
      </Box>

      {/* Custom Input Dialog */}
      <Dialog open={customInputDialogOpen} onClose={() => {
        setCustomInputDialogOpen(false);
        resetDialogFields();
      }} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit Input' : 'Add Input'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {isEditMode 
              ? 'Update the input configuration. You can change all fields except the type.'
              : 'Define a new input that can be used in your prompts. Inputs will be provided at runtime based on their type.'
            }
          </Typography>
          <Stack spacing={2} mt={1}>
            <TextField
              autoFocus
              fullWidth
              label="Input Name"
              value={newInputName}
              onChange={(e) => setNewInputName(e.target.value)}
              placeholder="e.g., user_query, market_data, previous_result"
              helperText="Use lowercase with underscores (snake_case)"
            />
            
            {/* Enhanced Input Type Selector with Grouping and Search */}
            <Autocomplete
              fullWidth
              disabled={isEditMode || availableInputTypesLoading || visibleInputTypeOptions.length === 0}
              options={visibleInputTypeOptions}
              groupBy={(option) => option.group}
              getOptionLabel={(option) => option.label}
              value={selectedVisibleInputTypeOption}
              onChange={(_e, newValue) => {
                if (newValue) {
                  handleInputTypeChange(newValue.value);
                }
              }}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Input Type"
                  placeholder="Search input types..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {availableInputTypesLoading ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  helperText={
                    isEditMode 
                      ? 'Input type cannot be changed after creation'
                      : availableInputTypesLoading
                        ? 'Loading available input handlers...'
                        : visibleInputTypeOptions.length === 0
                          ? inputTypesUnavailableMessage
                          : 'Search or browse input types by category'
                  }
                />
              )}
              renderGroup={(params) => {
                const groupInfo = INPUT_TYPE_GROUPS.find(g => g.name === params.group);
                return (
                  <li key={params.key}>
                    <Box
                      sx={{
                        position: 'sticky',
                        top: '-8px',
                        padding: '8px 16px',
                        backgroundColor: 'background.paper',
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        zIndex: 1,
                      }}
                    >
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontWeight: 'bold',
                          color: groupInfo?.color || 'text.secondary',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        {groupInfo?.icon} {params.group}
                      </Typography>
                    </Box>
                    <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
                  </li>
                );
              }}
              renderOption={(props, option) => {
                const groupInfo = INPUT_TYPE_GROUPS.find(g => g.name === option.group);
                return (
                  <li {...props} key={option.value}>
                    <Box sx={{ width: '100%', py: 0.5 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: groupInfo?.color || 'grey.400',
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" fontWeight="medium">
                          {option.label}
                        </Typography>
                      </Box>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ pl: 2, display: 'block' }}
                      >
                        {option.description}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              slotProps={{
                paper: {
                  sx: {
                    maxHeight: 400,
                    '& .MuiAutocomplete-listbox': {
                      padding: 0,
                    },
                  },
                },
              }}
            />

            {/* Dynamic fields based on input type - using shared InputConfigFields component */}
            <InputConfigFields
              inputType={newInputType}
              config={dynamicFields}
              onConfigChange={(newConfig) => {
                setDynamicFields(newConfig);
                // Sync indicators if present
                if (newConfig.indicators) {
                  setSelectedIndicators(newConfig.indicators);
                }
              }}
              availableExperts={availableExperts}
              disabled={disabled}
              expertInputs={
                availableInputs
                  .filter((inp) => inp.type === 'expert' && inp.expert_id)
                  .map((inp): ExpertInput => ({
                    name: inp.name,
                    expert_id: inp.expert_id!,
                    expert_name: inp.expert_name,
                  }))
              }
            />

            <TextField
              fullWidth
              label="Description (optional)"
              value={newInputDescription}
              onChange={(e) => setNewInputDescription(e.target.value)}
              placeholder="Describe what this input is for..."
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCustomInputDialogOpen(false);
            resetDialogFields();
          }}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleTestInput}
            disabled={isTesting || !newInputType || !systemId || newInputType === 'event_payload'}
            startIcon={isTesting ? <CircularProgress size={16} /> : <TestIcon />}
          >
            Test Input
          </Button>
          <Button
            onClick={handleAddCustomInput}
            variant="contained"
            disabled={
              !newInputName.trim() || 
              availableInputs.some(inp => 
                inp.name === newInputName.trim() && (!isEditMode || inp.name !== editingInput?.name)
              ) ||
              (!isEditMode && !selectedVisibleInputTypeOption) ||
              // Require symbol and timeframe for indicator, chart_image and price_data inputs
              ((newInputType === 'indicator' || newInputType === 'chart_image' || newInputType === 'price_data') && (!dynamicFields.symbol || !dynamicFields.timeframe))
            }
          >
            {isEditMode ? 'Update Input' : 'Add Input'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fullscreen Editor Dialog */}
      <Dialog
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        maxWidth={false}
        fullWidth
        fullScreen
        PaperProps={{
          sx: {
            height: '100vh',
            maxHeight: '100vh',
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{label}</Typography>
            <IconButton onClick={() => setIsFullscreen(false)} size="small">
              <FullscreenExitIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden', flex: 1, minHeight: 0 }}>
          {/* Mobile: toggle button for inputs panel in fullscreen */}
          {isMobile && (
            (showInputs && (availableInputs.length > 0 || sharedInputs.length > 0)) ||
            (showSnippets && snippets.length > 0)
          ) && (
            <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', gap: 1 }}>
              {showInputs && (availableInputs.length > 0 || sharedInputs.length > 0) && (
                <Button
                  size="small"
                  variant={showInputsPanel ? 'contained' : 'outlined'}
                  onClick={() => setShowInputsPanel(!showInputsPanel)}
                >
                  {showInputsPanel ? 'Hide Inputs' : `Inputs (${availableInputs.length + sharedInputs.length})`}
                </Button>
              )}
              {showSnippets && snippets.length > 0 && (
                <Button
                  size="small"
                  variant={showSnippetPanel ? 'contained' : 'outlined'}
                  color="success"
                  onClick={() => setShowSnippetPanel(!showSnippetPanel)}
                >
                  {showSnippetPanel ? 'Hide Snippets' : `Snippets (${snippets.length})`}
                </Button>
              )}
            </Box>
          )}
            {showInputs && !isEditMode && !availableInputTypesLoading && visibleInputTypeOptions.length === 0 && (
              <Alert severity={availableInputTypesError ? 'error' : 'warning'}>
                {inputTypesUnavailableMessage}
              </Alert>
            )}
          <Box display="flex" sx={{ flex: 1, minHeight: 0, overflow: 'hidden', flexDirection: isMobile ? 'column' : 'row' }}>
            {/* Available Inputs Sidebar - Always show in fullscreen */}
            {showInputs && (availableInputs.length > 0 || sharedInputs.length > 0) && (
              <Collapse
                in={isMobile ? showInputsPanel : true}
                sx={{
                  width: isMobile ? '100%' : 'auto',
                  flexShrink: 0,
                  height: isMobile ? 'auto' : '100%',
                  '& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner': {
                    height: isMobile ? 'auto' : '100%',
                  },
                }}
              >
              <Paper
                sx={{
                  width: isMobile ? '100%' : 280,
                  height: isMobile ? 'auto' : '100%',
                  p: isMobile ? 1.5 : 2,
                  bgcolor: isDark ? 'grey.900' : 'grey.50',
                  border: 1,
                  borderColor: isDark ? 'grey.700' : 'grey.300',
                  overflowY: 'auto',
                  flexShrink: 0,
                  ...(isMobile && { maxHeight: 250 }),
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  AVAILABLE INPUTS
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                  Click to insert into editor
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={1}>
                  {availableInputs.map((input) => (
                    <Paper
                      key={input.name}
                      sx={{
                        p: 1.5,
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: isDark ? 'grey.700' : 'grey.300',
                        '&:hover': {
                          bgcolor: isDark ? 'action.hover' : 'primary.50',
                          borderColor: 'primary.main',
                        },
                        transition: 'all 0.2s',
                      }}
                      onClick={() => insertInput(input.name)}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                          {input.name}
                        </Typography>
                        <Box display="flex" gap={0.5}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditInput(input);
                              }}
                              sx={{ p: 0.5 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveInput(input.name);
                              }}
                              sx={{ p: 0.5 }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      <Chip 
                        label={INPUT_TYPE_LABELS[input.type]} 
                        size="small" 
                        color={INPUT_TYPE_COLORS[input.type]}
                        sx={{ alignSelf: 'flex-start' }}
                      />
                      {input.description && (
                        <Typography variant="caption" color="text.secondary">
                          {input.description}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Stack>

                {/* Shared Inputs Section (fullscreen) */}
                {sharedInputs.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" fontWeight="bold" color="info.main" gutterBottom>
                      SHARED INPUTS
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                      System-level inputs (read-only)
                    </Typography>
                    <Stack spacing={1}>
                      {sharedInputs.map((input) => (
                        <Paper
                          key={`shared-fs-${input.name}`}
                          sx={{
                            p: 1.5,
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            bgcolor: isDark ? 'info.900' : 'info.50',
                            border: 1,
                            borderColor: isDark ? 'info.700' : 'info.200',
                            '&:hover': {
                              bgcolor: isDark ? 'info.800' : 'info.100',
                              borderColor: 'info.main',
                            },
                            transition: 'all 0.2s',
                          }}
                          onClick={() => {
                            const variable = `{{ shared.${input.name} }}`;
                            const editor = editorRef.current;
                            if (editor) {
                              const selection = editor.getSelection();
                              const id = { major: 1, minor: 1 };
                              const op = { identifier: id, range: selection, text: variable, forceMoveMarkers: true };
                              editor.executeEdits('prompt-editor', [op]);
                            }
                          }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography
                              variant="body2"
                              fontFamily="monospace"
                              fontWeight="bold"
                              color={isDark ? 'info.light' : 'info.dark'}
                            >
                              shared.{input.name}
                            </Typography>
                            <Tooltip title="Copy variable">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(`{{ shared.${input.name} }}`);
                                }}
                                sx={{ p: 0.5 }}
                              >
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Chip
                            label={INPUT_TYPE_LABELS[input.input_type as InputType] || input.input_type}
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ alignSelf: 'flex-start' }}
                          />
                          {input.description && (
                            <Typography variant="caption" color="text.secondary">
                              {input.description}
                            </Typography>
                          )}
                        </Paper>
                      ))}
                    </Stack>
                  </>
                )}

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setCustomInputDialogOpen(true)}
                  sx={{ mt: 2 }}
                >
                  Add Input
                </Button>
              </Paper>
              </Collapse>
            )}

            {/* Available Snippets Sidebar - Show in fullscreen if enabled */}
            {showSnippets && showSnippetPanel && snippets.length > 0 && (
              <Collapse
                in={isMobile ? showSnippetPanel : true}
                sx={{
                  width: isMobile ? '100%' : 'auto',
                  flexShrink: 0,
                  height: isMobile ? 'auto' : '100%',
                  '& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner': {
                    height: isMobile ? 'auto' : '100%',
                  },
                }}
              >
              <Paper
                sx={{
                  width: isMobile ? '100%' : 280,
                  height: isMobile ? 'auto' : '100%',
                  p: isMobile ? 1.5 : 2,
                  bgcolor: isDark ? 'success.900' : 'success.50',
                  border: 1,
                  borderColor: isDark ? 'success.700' : 'success.300',
                  overflowY: 'auto',
                  flexShrink: 0,
                  ...(isMobile && { maxHeight: 200 }),
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  AVAILABLE SNIPPETS
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                  Click to insert or drag into editor
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={1} sx={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
                  {snippets.map((snippet) => (
                    <Paper
                      key={snippet.id}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('snippetText', `[[snippet:${snippet.name}]]`);
                        e.dataTransfer.effectAllowed = 'copy';
                      }}
                      sx={{
                        p: 1.5,
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: isDark ? 'success.700' : 'success.300',
                        '&:hover': {
                          bgcolor: isDark ? 'action.hover' : 'success.50',
                          borderColor: 'success.main',
                        },
                        transition: 'all 0.2s',
                      }}
                      onClick={() => {
                        if (editorRef.current) {
                          const position = editorRef.current.getPosition();
                          const text = `[[snippet:${snippet.name}]]`;
                          if (position) {
                            editorRef.current.executeEdits('insert-snippet', [{
                              range: {
                                startLineNumber: position.lineNumber,
                                startColumn: position.column,
                                endLineNumber: position.lineNumber,
                                endColumn: position.column,
                              },
                              text,
                            }]);
                            editorRef.current.focus();
                          }
                        }
                      }}
                    >
                      <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                        {snippet.name}
                      </Typography>
                      {snippet.description && (
                        <Typography variant="caption" color="text.secondary">
                          {snippet.description}
                        </Typography>
                      )}
                      <Box display="flex" gap={1} alignItems="center" mt={0.5}>
                        {snippet.category && (
                          <Chip 
                            label={snippet.category} 
                            size="small" 
                            color="success"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        )}
                        <Typography variant="caption" color="text.secondary">
                          Used {snippet.usage_count || 0}x
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              </Paper>
              </Collapse>
            )}

            {/* Fullscreen Editor */}
            <Box flex={1} display="flex" flexDirection="column" sx={{ overflow: 'hidden', position: 'relative', minHeight: isMobile ? 300 : 'auto' }}>
              <Paper
                sx={{
                  flex: 1,
                  border: 0,
                  borderRadius: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Editor
                  height="100%"
                  defaultLanguage="prompt-template"
                  theme={isDark ? 'prompt-theme-dark' : 'prompt-theme-light'}
                  value={value}
                  onChange={(val) => onChange(val || '')}
                  beforeMount={handleEditorWillMount}
                  onMount={handleEditorDidMount}
                  options={{
                    minimap: { enabled: !isMobile },
                    lineNumbers: isMobile ? 'off' : 'on',
                    scrollBeyondLastLine: false,
                    wordWrap: 'on',
                    wrappingStrategy: 'advanced',
                    fontSize: isMobile ? 14 : 16,
                    fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
                    readOnly: disabled,
                    padding: { top: isMobile ? 10 : 20, bottom: isMobile ? 10 : 20 },
                    suggestOnTriggerCharacters: true,
                    quickSuggestions: true,
                    tabCompletion: 'on',
                    folding: !isMobile,
                    glyphMargin: !isMobile,
                  }}
                  loading={<Box p={4}>Loading editor...</Box>}
                />
              </Paper>
              
              {/* Fullscreen Info Bar */}
              <Paper sx={{ p: isMobile ? 0.5 : 1, borderRadius: 0, bgcolor: isDark ? 'grey.900' : 'grey.100', borderTop: 1, borderColor: isDark ? 'grey.700' : 'grey.300' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    {detectedVariables.length} input{detectedVariables.length !== 1 ? 's' : ''} detected
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isMobile ? 'Tap close to exit' : <>Press <code>Esc</code> or click the close button to exit fullscreen</>}
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Test Result Dialog */}
      <Dialog 
        open={testResultDialogOpen} 
        onClose={() => setTestResultDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Test Input Result
          {testResult && (
            <Chip
              size="small"
              label={testResult.success ? 'Success' : 'Error'}
              color={testResult.success ? 'success' : 'error'}
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {testResult && (
            <Stack spacing={2}>
              {/* Metadata */}
              <Box display="flex" gap={2} flexWrap="wrap">
                <Chip 
                  label={`Type: ${newInputType}`} 
                  size="small" 
                  variant="outlined"
                />
                <Chip 
                  label={`Execution: ${testResult.execution_time_ms}ms`} 
                  size="small" 
                  variant="outlined"
                  color="info"
                />
                {testResult.data_type && (
                  <Chip 
                    label={`Data Type: ${testResult.data_type}`} 
                    size="small" 
                    variant="outlined"
                  />
                )}
                {testResult.data_length !== undefined && (
                  <Chip 
                    label={`Length: ${testResult.data_length.toLocaleString()} chars`} 
                    size="small" 
                    variant="outlined"
                  />
                )}
                {testResult.truncated && (
                  <Chip 
                    label="Truncated" 
                    size="small" 
                    color="warning"
                  />
                )}
              </Box>

              {/* Result Data or Error */}
              {testResult.success ? (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Output Data:
                  </Typography>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      maxHeight: 400, 
                      overflow: 'auto',
                      bgcolor: isDark ? 'grey.900' : 'grey.50',
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {testResult.data || '(empty result)'}
                  </Paper>
                </Box>
              ) : (
                <Box>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">
                      {testResult.error_type}: {testResult.error}
                    </Typography>
                  </Alert>
                  {testResult.traceback && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Stack Trace:
                      </Typography>
                      <Paper 
                        variant="outlined" 
                        sx={{ 
                          p: 2, 
                          maxHeight: 300, 
                          overflow: 'auto',
                          bgcolor: isDark ? 'grey.800' : 'grey.100',
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {testResult.traceback}
                      </Paper>
                    </Box>
                  )}
                </Box>
              )}

              {/* Copy Button for successful results */}
              {testResult.success && testResult.data && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CopyIcon />}
                  onClick={() => {
                    navigator.clipboard.writeText(testResult.data || '');
                    toast.success('Output copied to clipboard');
                  }}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Copy Output
                </Button>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestResultDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
